import { useMemo } from 'react';

// Styles
import cornerStyles from '../../../styles/tableCardCorners.module.css';
import styles from './TableSubHeader.module.css';

// Utils
import cn from '../../../utils/cn';

// Domain
import { buildTableHeadRows } from '../../../domain/table/buildTableHeadRows';
import {
  buildLeafColumnIdIndexMap,
  footerStatHeaderOverlapsHoveredLeafIndicesFromMap,
} from '../../../domain/table/tableColumnHoverUseCase';
import { useTableColumnHoverStateOptional } from '../../../domain/table/useTableColumnHover';
import {
  getCommonPinningStyles,
  tableHasVisibleColumnStatValues,
} from '../../../domain/table/tableUseCase';
import {
  getPinnedAwareVisibleLeafColumns,
  getThSpanWidthPx,
} from '../../../domain/table/tableHeadUseCase';

/**
 * Global column totals row (API `columns[].statValue`), visually detached below the sticky table head.
 * @param {object} params
 * @param {TableInstance} params.table - Table instance
 * @returns {JSX.Element | null}
 */
function TableSubHeader({ table }) {
  const showSubHeader = tableHasVisibleColumnStatValues(table);
  const { hoveredLeafIndices } = useTableColumnHoverStateOptional();

  const { columnVisibility, columnOrder, columnPinning } = table.getState();

  const hoverLeafLookup = useMemo(() => {
    const leaves = getPinnedAwareVisibleLeafColumns(table);
    return {
      leafCount: leaves.length,
      columnIdToIndex: buildLeafColumnIdIndexMap(leaves),
    };
  }, [table, columnVisibility, columnOrder, columnPinning]);

  const columnIdToStatAlign = useMemo(() => {
    const leaves = getPinnedAwareVisibleLeafColumns(table);
    const headRows = buildTableHeadRows(leaves);
    const lastRow = headRows[headRows.length - 1];
    if (!lastRow) {
      return new Map();
    }
    return new Map(
      lastRow.cells.map((c) => [c.columnId, c.header.align ?? 'left']),
    );
  }, [table, columnVisibility, columnOrder, columnPinning]);

  const justifyFromAlign = (align) => {
    if (align === 'right') return 'flex-end';
    if (align === 'center') return 'center';
    return 'flex-start';
  };

  const headerGroups = table.getHeaderGroups();
  const leafHeaderGroup = headerGroups[headerGroups.length - 1];
  if (!showSubHeader || !leafHeaderGroup) {
    return null;
  }

  let leafStartIndex = 0;

  return (
    <div className={styles['table-sub-header']}>
      <table className={styles['table-sub-header-table']}>
        <thead>
          <tr className={styles['table-sub-header-row']} key={leafHeaderGroup.id}>
            {/** @type {TableHeader[]} */ (leafHeaderGroup.headers).map((header) => {
              const isColumnCheckbox = header.column.columnDef?.type === 'static-checkbox';
              const colSpan = isColumnCheckbox ? 1 : header.colSpan;
              const isFirstLeaf = leafStartIndex === 0;
              const isLastLeaf = leafStartIndex + colSpan === hoverLeafLookup.leafCount;
              leafStartIndex += colSpan;
              const pinStyles = getCommonPinningStyles(header.column);
              const widthPx = getThSpanWidthPx(table, header.column.id, colSpan);
              const mergedStyle = {
                ...pinStyles,
                width: widthPx,
                maxWidth: widthPx,
              };
              const isColumnHovered = footerStatHeaderOverlapsHoveredLeafIndicesFromMap(
                hoverLeafLookup.leafCount,
                hoverLeafLookup.columnIdToIndex,
                header,
                hoveredLeafIndices,
              );
              const typedColumnDef = /** @type {TableColumnsReactTable} */ (header.column.columnDef);
              const statRaw = typedColumnDef.statValue;
              const hasStatValue = statRaw != null && String(statRaw).trim() !== '';
              const align = columnIdToStatAlign.get(header.column.id) ?? 'left';
              const statText = hasStatValue ? String(statRaw).trim() : '';
              const statDisplay = isColumnCheckbox ? '\u00a0' : (statText || '\u00a0');

              return (
                <th
                  className={cn([
                    styles['table-sub-header-cell'],
                    header.column.getIsPinned() && styles['table-sub-header-cell-pinned'],
                    isColumnCheckbox && styles['table-sub-header-cell-checkbox'],
                    isColumnHovered && hasStatValue && styles['table-sub-header-cell-column-hover'],
                    isFirstLeaf && cornerStyles['corner-tl'],
                    isLastLeaf && cornerStyles['corner-tr'],
                  ])}
                  key={header.id}
                  colSpan={colSpan}
                  style={mergedStyle}
                >
                  {isFirstLeaf ? (
                    <span
                      className={cn([styles['table-sub-header-corner'], cornerStyles['corner-bl']])}
                      aria-hidden="true"
                    />
                  ) : null}
                  {isLastLeaf ? (
                    <span
                      className={cn([styles['table-sub-header-corner'], cornerStyles['corner-br']])}
                      aria-hidden="true"
                    />
                  ) : null}
                  <div className={styles['table-sub-header-cell-button']}>
                    <div
                      className={styles['table-sub-header-cell-content']}
                      style={{
                        textAlign: align,
                        justifyContent: justifyFromAlign(align),
                      }}
                    >
                      {statDisplay}
                    </div>
                  </div>
                </th>
              );
            })}
          </tr>
        </thead>
      </table>
    </div>
  );
}

export default TableSubHeader;
